import React, { useEffect, useRef } from 'react'
import { Drawer, Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import PrintIcon from '@mui/icons-material/Print'
import DescriptionIcon from '@mui/icons-material/Description'
import styled from 'styled-components'
import ChatContainer from '../Chat/ChatContainer'

const DrawerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
`

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.98) 0%, rgba(99, 102, 241, 0.98) 100%);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 2px 8px rgba(67, 56, 202, 0.2);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  min-height: 44px;
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    min-height: 40px;
  }
`

const DrawerTitle = styled(Typography)`
  font-size: 16px;
  font-weight: 700;
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  letter-spacing: -0.02em;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 15px;
    gap: 6px;
  }
`

const DrawerContent = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 12px 0px; /* Remove horizontal padding on mobile for full width content */
  }
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(67, 56, 202, 0.08);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.3);
    border-radius: 6px;
    border: 2px solid rgba(67, 56, 202, 0.08);
    
    &:hover {
      background: rgba(67, 56, 202, 0.5);
    }
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(67, 56, 202, 0.3) rgba(67, 56, 202, 0.08);
`

// Icon mapping for menu items
const getSectionIcon = (itemId) => {
  const iconMap = {
    patient_history: HistoryIcon,
    'report-preview': PrintIcon,
    chat: DescriptionIcon,
  }
  return iconMap[itemId] || DescriptionIcon
}

const SessionDrawer = ({
  open,
  onClose,
  selectedMenuItem,
  menuItemsList,
  renderWhatINeed,
  chatProps,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const drawerRef = useRef(null)
  
  
  // Find the current menu item to get its label
  const currentMenuItem = menuItemsList?.find((item) => item.id === selectedMenuItem)
  const sectionTitle = currentMenuItem?.label || 'Section'
  const SectionIcon = getSectionIcon(selectedMenuItem)
  
  // Determine if this is a secondary section based on position (not secondary property)
  // Primary section is the first non-video_call item, all others are secondary
  const filteredMenuItems = menuItemsList?.filter((item) => item?.id !== 'video_call') || []
  const primarySectionId = filteredMenuItems[0]?.id
  const isSecondarySection = selectedMenuItem !== primarySectionId && selectedMenuItem !== 'chat' || selectedMenuItem === 'chat'
  
  
  // Always render the Drawer component - let MUI handle the open/close animation
  // Don't return null as that prevents the drawer from being in the DOM

  // Fix aria-hidden accessibility issue
  // When drawer is open and has focusable content, ensure root element doesn't have aria-hidden
  useEffect(() => {
    if (!open) {
      // When drawer closes, ensure root doesn't have aria-hidden
      const root = document.getElementById('root')
      if (root) {
        root.removeAttribute('aria-hidden')
      }
      return
    }

    // Use a small delay to ensure MUI has finished rendering the drawer
    const timer = setTimeout(() => {
      // Find the root element
      const root = document.getElementById('root')
      if (!root) return

      // Check if drawer has focusable content
      if (drawerRef.current) {
        const drawerPaper = drawerRef.current.closest('.MuiDrawer-paper')
        const hasFocusableContent = drawerPaper?.querySelector(
          'input:not([disabled]), button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
        
        // If drawer has focusable content, remove aria-hidden from root
        // MUI sets this to hide background content, but it violates accessibility if focused element is inside
        if (hasFocusableContent) {
          root.removeAttribute('aria-hidden')
        }
      }

      // Also check for any focused element inside the drawer
      const activeElement = document.activeElement
      if (activeElement && drawerRef.current?.contains(activeElement)) {
        // If there's a focused element inside drawer, remove aria-hidden from root
        root.removeAttribute('aria-hidden')
      }
    }, 100)

    // Also listen for focus events inside the drawer
    const handleFocus = (e) => {
      if (drawerRef.current?.contains(e.target)) {
        const root = document.getElementById('root')
        if (root) {
          root.removeAttribute('aria-hidden')
        }
      }
    }

    document.addEventListener('focusin', handleFocus)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('focusin', handleFocus)
    }
  }, [open])

  
  // Force visibility when drawer is open - fix for Material-UI setting visibility: hidden
  // Use MutationObserver to catch when Material-UI changes styles
  React.useEffect(() => {
    if (!open || !isSecondarySection) {
      return
    }
    
    // Start finding elements with a delay to ensure DOM is ready
    let drawerPaper = null
    let drawerModal = null
    // Capture isMobile value for use in enforceVisibility
    const currentIsMobile = isMobile
    
    const setupVisibilityEnforcement = () => {
      const drawerContainer = drawerRef.current
      if (!drawerContainer) {
        return false
      }
      
      drawerPaper = drawerContainer.closest('.MuiDrawer-paper')
      drawerModal = drawerContainer.closest('.MuiModal-root')
      
      if (!drawerPaper || !drawerModal) {
        return false
      }
      
      return true
    }
    
    // Try to setup immediately, then retry if needed
    let setupAttempts = 0
    const trySetup = () => {
      if (setupVisibilityEnforcement()) {
        // Elements found, proceed with enforcement
        startEnforcement()
      } else if (setupAttempts < 20) {
        setupAttempts++
        setTimeout(trySetup, 50)
      }
    }
    
    const startEnforcement = () => {
      if (!drawerPaper || !drawerModal) return
      
      let lastEnforceTime = 0
      const ENFORCE_THROTTLE_MS = 100 // Throttle enforcement to avoid loops
      
      const enforceVisibility = () => {
        const now = Date.now()
        if (now - lastEnforceTime < ENFORCE_THROTTLE_MS) {
          return // Skip if called too recently
        }
        lastEnforceTime = now
        
        if (!drawerModal || !drawerPaper) {
          return // Elements not ready yet
        }
        
        // Check if elements are actually in the DOM
        if (!drawerModal.isConnected || !drawerPaper.isConnected) {
          return // Elements not in DOM yet
        }
        
        // Remove MUI classes that hide it FIRST (before setting styles)
        drawerModal.classList.remove('MuiModal-hidden')
        
        // Add a custom class to identify this drawer
        drawerModal.classList.add('session-drawer-open')
        
        // Always force visibility and display when drawer is open
        // For right-side drawer, we need to ensure it's positioned correctly
        // Material-UI uses transform to slide drawers in/out
        drawerPaper.style.setProperty('visibility', 'visible', 'important')
        drawerPaper.style.setProperty('display', 'flex', 'important')
        drawerPaper.style.setProperty('opacity', '1', 'important')
        // For bottom drawer: translateY(0) to bring it on-screen from bottom
        drawerPaper.style.setProperty('transform', 'translateY(0)', 'important')
        drawerPaper.style.setProperty('z-index', '1300', 'important')
        // Ensure drawer paper is positioned correctly for bottom drawer
        drawerPaper.style.setProperty('position', 'fixed', 'important')
        drawerPaper.style.setProperty('left', '0', 'important')
        drawerPaper.style.setProperty('right', '0', 'important')
        drawerPaper.style.setProperty('bottom', '0', 'important')
        drawerPaper.style.setProperty('top', 'auto', 'important')
        drawerPaper.style.setProperty('height', currentIsMobile ? '95vh' : '85vh', 'important')
        drawerPaper.style.setProperty('max-height', currentIsMobile ? '95vh' : '85vh', 'important')
        // CRITICAL: Set width - full width for bottom drawer to accommodate horizontal content
        drawerPaper.style.setProperty('width', '100%', 'important')
        drawerPaper.style.setProperty('max-width', '100%', 'important')
        drawerPaper.style.setProperty('min-width', '100%', 'important')
        
        drawerModal.style.setProperty('visibility', 'visible', 'important')
        drawerModal.style.setProperty('display', 'block', 'important')
        drawerModal.style.setProperty('opacity', '1', 'important')
        drawerModal.style.setProperty('z-index', '1300', 'important')
        
        // Force backdrop visibility
        const backdrop = drawerModal.querySelector('.MuiBackdrop-root')
        if (backdrop) {
          backdrop.style.setProperty('visibility', 'visible', 'important')
          backdrop.style.setProperty('display', 'block', 'important')
          backdrop.style.setProperty('opacity', '1', 'important')
          backdrop.style.setProperty('z-index', '1200', 'important')
        }
        
      }
      
      // Run immediately with a small delay to ensure DOM is ready
      setTimeout(() => {
        enforceVisibility()
      }, 10)
      
      // Use MutationObserver to catch when Material-UI changes the display/visibility
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
            if (!drawerModal) return
            
            const modalDisplay = window.getComputedStyle(drawerModal).display
            const modalVisibility = window.getComputedStyle(drawerModal).visibility
            if (modalDisplay === 'none' || modalVisibility === 'hidden') {
              enforceVisibility()
            }
          }
        })
      })
      
      // Observe the modal for style/class changes
      observer.observe(drawerModal, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
      
      // Also use interval as backup - but less frequently to avoid performance issues
      const interval = setInterval(() => {
        // Only enforce if drawer is still open and elements exist
        if (open && drawerModal && drawerPaper && 
            drawerModal.isConnected && drawerPaper.isConnected) {
          enforceVisibility()
        }
      }, 500) // Reduced frequency
      
      return () => {
        observer.disconnect()
        clearInterval(interval)
      }
    }
    
    // Start the setup process
    trySetup()
    
    return () => {
      // Cleanup if component unmounts
    }
  }, [open, isSecondarySection, selectedMenuItem])
  
  // Add global CSS to force visibility when drawer should be open
  React.useEffect(() => {
    if (!open) return
    
    // Inject a style tag to override Material-UI's hiding behavior
    const styleId = 'session-drawer-force-visible'
    let styleElement = document.getElementById(styleId)
    
    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      document.head.appendChild(styleElement)
    }
    
    styleElement.textContent = `
      .session-drawer-open,
      .session-drawer-open .MuiModal-root,
      .session-drawer-open .MuiDrawer-paper,
      .session-drawer-open .MuiBackdrop-root {
        visibility: visible !important;
        display: block !important;
        opacity: 1 !important;
      }
      .session-drawer-open .MuiDrawer-paper {
        display: flex !important;
        transform: translateY(0) !important;
        position: fixed !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        top: auto !important;
        width: 100% !important;
        max-width: 100% !important;
        height: 85vh !important;
        max-height: 85vh !important;
      }
      .session-drawer-open.MuiDrawer-root[data-anchor="bottom"] .MuiDrawer-paper {
        transform: translateY(0) !important;
      }
    `
    
    return () => {
      // Don't remove the style element - it might be needed for other drawers
    }
  }, [open])
  
  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'bottom'} // Changed to bottom for desktop to solve horizontal cutoff
      open={open}
      onClose={onClose}
      className='hide-on-print-only session-drawer'
      variant="temporary" // Explicitly set variant
      transitionDuration={0} // Disable transition to prevent Material-UI from hiding it
      hideBackdrop={false} // Ensure backdrop is shown
      keepMounted={true} // Keep drawer mounted to prevent Material-UI from removing it
      ModalProps={{
        disableScrollLock: true, // Don't lock body scroll - allow main page to scroll
        disableEnforceFocus: false, // Allow focus management
        disableAutoFocus: false, // Allow auto-focus on open
        // Prevent aria-hidden from being set on backdrop when drawer has focus
        hideBackdrop: false,
      }}
      BackdropProps={{
        sx: {
          // Make backdrop slightly transparent
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          // Ensure backdrop doesn't block clicks to tabs (tabs have z-index 1300)
          zIndex: 1200, // Standard MUI drawer backdrop z-index
          // Allow pointer events to pass through to tabs
          pointerEvents: 'none', // Don't block clicks - tabs can be clicked through backdrop
        },
      }}
      PaperProps={{
        sx: {
          // For bottom drawer: full width for horizontal content (prescription pages)
          width: '100%',
          maxWidth: '100%',
          // Height constraints for bottom drawer
          height: isMobile ? '95vh' : '85vh', // Use 85vh on desktop to leave some space
          maxHeight: isMobile ? '95vh' : '85vh',
          borderTopLeftRadius: isMobile ? '20px' : '20px', // Rounded top corners for bottom drawer
          borderTopRightRadius: isMobile ? '20px' : '20px',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          boxShadow: '0 -4px 24px rgba(67, 56, 202, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.1)', // Shadow from top
          // Ensure drawer paper can receive pointer events
          pointerEvents: open ? 'auto' : 'none',
          zIndex: 1300, // Ensure drawer is above backdrop
          // Force visibility when open - override any MUI transition styles
          visibility: open ? 'visible !important' : 'hidden',
          display: open ? 'flex !important' : 'none',
          transition: 'none !important', // Disable transitions
          // For bottom drawer: translateY(0) when open (slides up from bottom)
          transform: open ? 'translateY(0) !important' : undefined,
        },
      }}
      sx={{
        zIndex: 1300, // Ensure drawer modal is above everything
        // When closed: ensure the Drawer root (Modal) does not block clicks or show
        ...(open ? {} : {
          pointerEvents: 'none !important',
          visibility: 'hidden !important',
          display: 'none !important',
        }),
        // Force modal visibility when open - override Material-UI's hidden state
        // Target the actual modal root element
        '&.MuiDrawer-root': {
          ...(open ? {} : {
            pointerEvents: 'none !important',
            visibility: 'hidden !important',
            display: 'none !important',
          }),
          '& .MuiModal-root': {
            visibility: open ? 'visible !important' : 'hidden',
            display: open ? 'block !important' : 'none',
            opacity: open ? '1 !important' : '0',
            pointerEvents: open ? 'auto' : 'none',
          },
          '& .MuiBackdrop-root': {
            visibility: open ? 'visible !important' : 'hidden',
            display: open ? 'block !important' : 'none',
            opacity: open ? '1 !important' : '0',
            pointerEvents: open ? 'auto' : 'none',
          },
          '& .MuiDrawer-paper': {
            visibility: open ? 'visible !important' : 'hidden',
            display: open ? 'flex !important' : 'none',
            opacity: open ? '1 !important' : '0',
            transform: open ? 'none !important' : undefined,
            pointerEvents: open ? 'auto' : 'none',
          },
        },
        // Also target when Material-UI adds the hidden class
        '&.MuiModal-hidden': {
          visibility: open ? 'visible !important' : 'hidden',
          display: open ? 'block !important' : 'none',
          opacity: open ? '1 !important' : '0',
        },
        // Target the modal root directly
        '& .MuiModal-root': {
          '&.MuiModal-hidden': {
            visibility: open ? 'visible !important' : 'hidden',
            display: open ? 'block !important' : 'none',
            opacity: open ? '1 !important' : '0',
          },
        },
      }}
    >
      <DrawerContainer ref={drawerRef}>
        {/* Header with title and close button */}
        <DrawerHeader>
          <DrawerTitle>
            <SectionIcon 
              sx={{ 
                fontSize: { xs: 18, md: 20 },
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
              }} 
            />
            {sectionTitle}
          </DrawerTitle>
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              if (onClose) {
                onClose(e, 'buttonClick')
              }
            }}
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px) saturate(150%)',
              WebkitBackdropFilter: 'blur(10px) saturate(150%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '8px',
              width: { xs: '32px', md: '36px' },
              height: { xs: '32px', md: '36px' },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                borderColor: 'rgba(255, 255, 255, 0.4)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
          </IconButton>
        </DrawerHeader>

        {/* Content area */}
        <DrawerContent>
          {!isSecondarySection ? (
            // Don't render anything if this is not a secondary section
            <Box 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                gap: 2,
              }}
            >
              <DescriptionIcon sx={{ fontSize: 64, color: 'rgba(67, 56, 202, 0.3)', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(67, 56, 202, 0.7)', mb: 1 }}>
                No Content Available
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(67, 56, 202, 0.6)' }}>
                This drawer is only for secondary sections.
              </Typography>
            </Box>
          ) : selectedMenuItem === 'chat' && chatProps?.peerChatId ? (
            <ChatContainer {...chatProps} showNotificationsOnIncomingMessage={false} />
          ) : (
                (() => {
                  try {
                    return renderWhatINeed(selectedMenuItem, onClose)
                  } catch (error) {
                    return (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="error">
                          Error loading content
                        </Typography>
                        <Typography variant="body2" color="error">
                          {error.message}
                        </Typography>
                      </Box>
                    )
                  }
                })()
          )}
        </DrawerContent>
      </DrawerContainer>
    </Drawer>
  )
}

export default SessionDrawer
